import { ApplicationConfigurationDto } from '@unistocks/proxy/axios-client';
import { useApplicationConfiguration } from '@unistocks/shared/hooks/use-application-configuration';

export const selectTenantId = ({ currentTenant }: ApplicationConfigurationDto) => currentTenant.id;
export const selectTenantAvailable = ({ currentTenant }: ApplicationConfigurationDto) =>
  currentTenant.isAvailable;
export const selectCurrentUser = ({ currentUser }: ApplicationConfigurationDto) => currentUser;
export const selectLocalization = ({ localization }: ApplicationConfigurationDto) => localization;
export const selectTiming = ({ timing }: ApplicationConfigurationDto) => timing;
export const selectCulture = ({ localization }: ApplicationConfigurationDto) =>
  localization?.currentCulture?.name;
export const selectLanguage = ({ localization }: ApplicationConfigurationDto) =>
  localization?.currentCulture.cultureName;
export const selectLanguages = ({ localization }: ApplicationConfigurationDto) =>
  localization?.languages ?? [];
export const selectSettings = ({ setting }: ApplicationConfigurationDto) => setting?.values ?? {};
export const selectFeatures = ({ features }: ApplicationConfigurationDto) => features.values ?? {};
export const selectPermissions = ({ auth }: ApplicationConfigurationDto) =>
  auth.grantedPolicies ?? {};

/**
 * Returns the tenant id of the logged in user.
 */
export const useTenantId = () => {
  const { data } = useApplicationConfiguration({ select: selectTenantId });

  return data;
};

/**
 * Returns true if tenant information is available, false otherwise. If false, it means the user is a host user (no tenant).
 */
export const useTenantAvailable = () => {
  const { data } = useApplicationConfiguration({ select: selectTenantAvailable });

  return data;
};

/**
 * Returns the current user of the application.
 */
export const useCurrentUser = () => {
  const { data } = useApplicationConfiguration({ select: selectCurrentUser });

  return data;
};

/**
 * Returns the current time zone of the application.
 */
export const useTimeZone = () => {
  const { data } = useApplicationConfiguration({ select: selectTiming });

  return data?.timeZone.iana.timeZoneName ?? undefined;
};

/**
 * Returns the current culture of the application.
 */
export const useLocalization = () => {
  const { data } = useApplicationConfiguration({ select: selectLocalization });

  return data;
};

/**
 * Returns the list of languages available in the application.
 */
export const useLanguages = () => {
  const { data } = useApplicationConfiguration({ select: selectLanguages });

  return data;
};

/**
 * Returns the current language of the application.
 */
export const useLanguage = () => {
  const { data } = useApplicationConfiguration({ select: selectLanguage });

  return data;
};

export const useSettings = () => {
  const { data } = useApplicationConfiguration({ select: selectSettings });

  return data;
};

export const useFeatures = () => {
  const { data } = useApplicationConfiguration({ select: selectFeatures });

  return data;
};

export const usePermissions = () => {
  const { data } = useApplicationConfiguration({ select: selectPermissions });

  return data;
};
